import {
    Button,
    Heading,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaCheck, FaEdit, FaPlus, FaSync } from "react-icons/fa";
import ImportCSVButton from "../../components/Buttons/ImportCSVButton";
import { HiCreditCard } from "react-icons/hi";

import { useSelector } from "react-redux";
import APIDropdown from "../../components/Select/APIDropdown";
import { GET, POST } from "../../utils/ApiRequestProvider";

export default function PaymentMethods() {
    const business = useSelector((state: any) => state.selections.value?.business);
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [items, setItems] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(true);

    const [form, setForm] = useState<any>({
        displayName: '',
        bankName: '',
        email: '',
        currencyId: 0,
        paymentType: '',
        status: '',
    });

    async function Save() {
        try {
            setLoading(true);

            const response = await POST(
                '/api/paymentmethods',
                {
                    ...form,
                    businessId: business.id
                }
            );
            toast({
                status: response.status,
                title: response.message,
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });

            setLoading(false);

            if (response.status === 'success') {
                FetchList();
                setForm({
                    displayName: '',
                    bankName: '',
                    email: '',
                    currencyId: 0,
                    paymentType: '',
                    status: '',
                });
                onClose();
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }
    
    async function CloseModel() {
        setForm({
            displayName: '',
            bankName: '',
            email: '',
            currencyId: 0,
            paymentType: '',
            status: '',
        });
        onClose();
    }

    async function Edit(data: any) {
        try {
            setForm({
                id: data.id,
                displayName: data.displayName,
                bankName: data.bankName,
                email: data.email,
                currencyId: data.defaultCurrency.id,
                paymentType: data.paymentType,
                status: data.status
            });
            onOpen();
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    async function RefreshPage() {
        setLoading(true);
        setTimeout(() => {FetchList();setLoading(false)}, 1000);
        
    }

    useEffect(() => {
        FetchList();
        setTimeout(() => {setLoading(false)}, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business]);
    

    async function FetchList() {
        try {
            if (business) {
                const res = await GET(`/api/paymentmethods/${business.id}`);
                setItems(res.data);
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
    }

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'row' }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 0 }}
                align={'center'}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={HiCreditCard}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Payment Methods</Heading>
                            <Text fontSize={'sm'}>Here you can see all your payment methods imported from online accounting softwares</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', xl: 'flex-end' }} justify={'end'} spacing={4}>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<FaPlus />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={onOpen}
                        >
                            Add Method
                        </Button>
                        <ImportCSVButton
                            endpoint={business ? `/api/paymentmethods/${business.id}/import` : '/api/paymentmethods/import'}
                            label={'Import Method'}
                            onImported={() => FetchList()}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                        />
                    </Stack>
                    <Stack direction={'row'}>
                        <Button
                            size={'sm'}
                            leftIcon={<FaSync />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={RefreshPage}
                            isLoading={loading}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <TableContainer h={'sm'} overflowY={'scroll'}>
                    <Table size='sm'>
                        <Thead position={'sticky'} top={'0%'} zIndex={1} h={'50px'} bg={'primary.400'} alignItems={'flex-start'}>
                            <Tr>
                                <Th
                                    color={'white'}
                                >
                                    Display Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Bank Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Email
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Default Currency
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Payment Type
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Status
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Action
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {
                                
                                loading ? 
                                (
                                    <Tr>
                                        <Td colSpan={7} textAlign={'center'}>Loading...</Td>
                                    </Tr>
                                ) :
                                items.length === 0 ?
                                    (
                                        <Tr>
                                            <Td colSpan={7} textAlign={'center'}>No data available</Td>
                                        </Tr>
                                    )
                                    :
                                    (
                                        items.map((item: any) => (
                                            <Tr>
                                                <Td>{item.displayName ?? item.name}</Td>
                                                <Td>{item.bankName ?? item.bank_name}</Td>
                                                <Td>{item.email ?? ''}</Td>
                                                <Td>{item.defaultCurrency?.currencyName ?? ''}</Td>
                                                <Td>{item.paymentType ?? item.type}</Td>
                                                <Td>{item.isActive === false ? 'Inactive' : 'Active'}</Td>
                                                <Td as={Stack} direction={'row'} justify={'center'} spacing={4}>
                                                    <IconButton
                                                        aria-label="Edit Contact"
                                                        icon={<FaEdit />}
                                                        size={'md'}
                                                        bg={'transparent'}
                                                        color={'primary.400'}
                                                        rounded={'full'}
                                                        onClick={() => Edit(item)}
                                                    />
                                                </Td>
                                            </Tr>
                                        ))
                                    )
                            }
                        </Tbody>
                    </Table>
                </TableContainer>
            </Stack>
            <Modal
                isCentered
                size={'3xl'}
                isOpen={isOpen}
                onClose={CloseModel}
            >
                <ModalOverlay
                    bg='blackAlpha.300'
                    backdropFilter='blur(10px)'
                />
                <ModalContent>
                    <ModalHeader
                        as={Stack}
                        direction={'row'}
                        borderBottom={'2px'}
                        borderBottomColor={'gray.300'}
                    >
                        <Icon
                            as={HiCreditCard}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Add Payment Methods</Heading>
                            <Text fontSize={'sm'}>Add new Payment Methods from here</Text>
                        </Stack>
                    </ModalHeader>
                    <ModalBody as={Stack} direction={'row'} spacing={4} py={8}>
                        <Stack spacing={4} w={'full'}>
                            <Input
                                w={'full'}
                                type={'text'}
                                size={'md'}
                                placeholder={'Display Name'}
                                value={form.displayName}
                                onChange={(e: any) => setForm({ ...form, displayName: e.target.value })}
                            />
                            <Input
                                w={'full'}
                                type={'text'}
                                size={'md'}
                                placeholder={'Email'}
                                value={form.email}
                                onChange={(e: any) => {
                                    setForm({
                                        ...form,
                                        email: e.target.value
                                    })
                                }}
                            />
                            <Input
                                w={'full'}
                                type={'text'}
                                size={'md'}
                                placeholder={'Payment Type'}
                                value={form.paymentType}
                                onChange={(e: any) => {
                                    setForm({
                                        ...form,
                                        paymentType: e.target.value
                                    })
                                }}
                            />
                        </Stack>
                        <Stack spacing={4} w={'full'}>
                            <Input
                                w={'full'}
                                type={'text'}
                                size={'md'}
                                placeholder={'Bank Name'}
                                value={form.bankName}
                                onChange={(e: any) => setForm({ ...form, bankName: e.target.value })}
                            />
                            <APIDropdown
                                w={'full'}
                                placeholder={'Default Currency'}
                                fetchurl={'/api/currency'}
                                itemselect={(item) => setForm({ ...form, currencyId: item.id })}
                                valuekey={'id'}
                                textkey={'currencyName'}
                            />
                            <Select
                                w={'full'}
                                placeholder={'Status'}
                                value={form.status}
                                onChange={(e: any) =>
                                    setForm({ ...form, status: e.target.value })
                                }
                            >
                                <option value={'Active'}>Active</option>
                                <option value={'Inactive'}>Inactive</option>
                            </Select>
                        </Stack>
                    </ModalBody>
                    <ModalFooter
                        as={Stack}
                        direction={'row'}
                        spacing={4}
                        borderTop={'2px'}
                        borderTopColor={'gray.300'}
                    >
                        <Button
                            w={'full'}
                            onClick={onClose}
                        >
                            Close
                        </Button>
                        <Button
                            w={'full'}
                            onClick={Save}
                            leftIcon={<FaCheck />}
                            colorScheme={'primary'}
                            isLoading={loading}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Stack >
    )
}